import React, { useMemo, useState, useEffect } from 'react'
import CodeSnippet from './CodeSnippet'
import ModButton from '../ui/ModButton/ModButton'
import { useLang } from '../lib/useLang'

export default function InferenceView({ mode, onBack, workflowId, tables = [], columns = [], taskType = null }) {
  const { t } = useLang()
  const isBatch = mode === 'batch'
  const title = isBatch ? t('inferenceView.batchInference') : t('inferenceView.onlineInference')
  const isTimeSeries = typeof taskType === 'string' && taskType.includes('time_series')

  const wfId = workflowId || '<WORKFLOW_ID>'
  const baseUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/api/inference`
    : 'https://modeller.tech/api/inference'

  // State for target_col from /predict/info
  const [targetCol, setTargetCol] = useState(null)

  // Fetch target_col from API on mount
  useEffect(() => {
    if (!workflowId) return

    const fetchInfo = async () => {
      try {
        const res = await fetch(`/api/inference/info?workflow_id=${encodeURIComponent(workflowId)}`)
        const data = await res.json()
        if (!data.error && data.target_col) {
          setTargetCol(data.target_col)
        }
      } catch (err) {
        console.error('Failed to fetch target_col:', err)
      }
    }

    fetchInfo()
  }, [workflowId])

  // Determine tables to use: from props.tables or fallback to legacy columns prop
  const columnsToUse = useMemo(() => {
    if (tables && tables.length > 0) {
      // Use tables from props (preferred)
      return tables
    } else if (columns.length > 0) {
      // Fallback to old columns prop (for backward compatibility, no table name)
      return [{ name: null, columns }]
    } else {
      return []
    }
  }, [tables, columns])

  // Filter out target column from all tables
  const filteredTables = useMemo(() => {
    if (!targetCol) return columnsToUse

    return columnsToUse.map(table => ({
      ...table,
      columns: (table.columns || []).filter(col => {
        const colName = typeof col === 'string' ? col : col?.name
        return colName !== targetCol
      })
    }))
  }, [columnsToUse, targetCol])

  // Memoize column names and sample values (use first table's columns for examples)
  const { columnNames, sampleValues, csvExampleEscaped } = useMemo(() => {
    // Get columns from first table (for single-table or example)
    const firstTable = filteredTables[0]
    const safeColumns = firstTable?.columns || []

    if (safeColumns.length === 0) {
      return {
        columnNames: ['feature1', 'feature2'],
        sampleValues: ['value1', 'value2'],
        csvExampleEscaped: 'feature1,feature2\\nvalue1,value2'
      }
    }

    const names = []
    const values = []

    for (const col of safeColumns) {
      const name = typeof col === 'string' ? col : (col?.name || 'column')
      const type = (typeof col === 'object' && col?.type) ? col.type.toLowerCase() : 'string'

      names.push(name)

      if (type === 'numeric' || type === 'int' || type === 'float' || type === 'number') {
        values.push('0.0')
      } else if (type === 'date' || type === 'datetime') {
        values.push('2024-01-01')
      } else if (type === 'boolean' || type === 'bool') {
        values.push('true')
      } else {
        values.push('value')
      }
    }

    return {
      columnNames: names,
      sampleValues: values,
      csvExampleEscaped: `${names.join(',')}\\n${values.join(',')}`
    }
  }, [filteredTables])

  // Pre-compute CSV lines for Python examples
  const csvHeader = columnNames.join(',')
  const csvDataRow = sampleValues.join(',')

  // Build a CSV "header\\nrow" example for an individual table — used by the
  // multi-table examples so each table shows ALL its columns with realistic
  // sample values inferred from column types, not the first three columns
  // with placeholder "value1,value2,value3".
  function buildTableCsvExample(tableObj, escape = 'curl') {
    const cols = (tableObj?.columns || [])
    if (cols.length === 0) {
      return escape === 'curl' ? 'col1,col2\\\\nvalue1,value2' : 'col1,col2\\nvalue1,value2'
    }
    const names = []
    const values = []
    for (const col of cols) {
      const name = typeof col === 'string' ? col : (col?.name || 'column')
      const type = (typeof col === 'object' && col?.type) ? String(col.type).toLowerCase() : 'string'
      names.push(name)
      if (type === 'numeric' || type === 'int' || type === 'float' || type === 'number') {
        values.push('0.0')
      } else if (type === 'date' || type === 'datetime') {
        values.push('2024-01-01')
      } else if (type === 'boolean' || type === 'bool') {
        values.push('true')
      } else {
        values.push('value')
      }
    }
    // curl uses \\\\n (double-escaped because it's inside a JSON string inside
    // a single-quoted shell argument), Python uses \\n inside a triple-quoted
    // or regular string literal.
    const sep = escape === 'curl' ? '\\\\n' : '\\n'
    return `${names.join(',')}${sep}${values.join(',')}`
  }

  // Init curl command
  const initCurl = `curl -X POST "${baseUrl}/init" \\
    -H "Content-Type: application/json" \\
    -H "Authorization: Bearer <YOUR_API_KEY>" \\
    -d '{"workflow_id": "${wfId}"}'`

  // Predict curl command (single-table vs multi-table)
  const isMultiTable = filteredTables.length > 1

  // For time-series — guess id_col, timestamp_col, target_col from columns
  const tsSchema = useMemo(() => {
    if (!isTimeSeries) return null
    const firstTable = filteredTables[0]
    const cols = firstTable?.columns || []
    let idCol = null, tsCol = null, tgtCol = targetCol || null
    for (const c of cols) {
      const name = (typeof c === 'string' ? c : c?.name || '').toLowerCase()
      const type = (typeof c === 'object' && c?.type) ? String(c.type).toLowerCase() : ''
      if (!idCol && (name === 'id' || name.endsWith('_id') || name.includes('series') || name.includes('item'))) {
        idCol = typeof c === 'string' ? c : c?.name
      }
      if (!tsCol && (type.includes('date') || type.includes('time') || name.includes('date') || name.includes('time'))) {
        tsCol = typeof c === 'string' ? c : c?.name
      }
    }
    return {
      id_col: idCol || 'series_id',
      timestamp_col: tsCol || 'timestamp',
      target_col: tgtCol || 'target',
    }
  }, [isTimeSeries, filteredTables, targetCol])

  // Time-series CSV: use all columns from first table with sample values
  const tsCsvExample = useMemo(() => {
    if (!tsSchema) return ''
    const firstTable = filteredTables[0]
    const cols = firstTable?.columns || []
    if (cols.length === 0) {
      const { id_col, timestamp_col, target_col } = tsSchema
      return `${id_col},${timestamp_col},${target_col}\\n1,2024-01-01,100.0\\n1,2024-01-02,102.5\\n1,2024-01-03,98.7`
    }
    // Build CSV with all columns and 3 sample rows
    return buildTableCsvExample(firstTable, 'curl')
  }, [tsSchema, filteredTables])

  const tsCsvExamplePython = useMemo(() => {
    if (!tsSchema) return ''
    const firstTable = filteredTables[0]
    const cols = firstTable?.columns || []
    if (cols.length === 0) {
      const { id_col, timestamp_col, target_col } = tsSchema
      return `${id_col},${timestamp_col},${target_col}\\n1,2024-01-01,100.0\\n1,2024-01-02,102.5`
    }
    return buildTableCsvExample(firstTable, 'python')
  }, [tsSchema, filteredTables])

  const predictCurl = isTimeSeries && isMultiTable
    ? `curl -X POST "${baseUrl}/predict" \\
    -H "Content-Type: application/json" \\
    -H "Authorization: Bearer <YOUR_API_KEY>" \\
    -d '{
      "workflow_id": "${wfId}",
      "tables": {
${filteredTables.map((t, i) => {
  const tableName = t.name?.replace(/\.csv$/i, '') || `table${i+1}`
  return `        "${tableName}": "${buildTableCsvExample(t, 'curl')}"`
}).join(',\n')}
      }
    }'`
    : isTimeSeries
    ? `curl -X POST "${baseUrl}/predict" \\
    -H "Content-Type: application/json" \\
    -H "Authorization: Bearer <YOUR_API_KEY>" \\
    -d '{
      "workflow_id": "${wfId}",
      "csv": "${tsCsvExample}"
    }'`
    : isMultiTable
    ? `curl -X POST "${baseUrl}/predict" \\
    -H "Content-Type: application/json" \\
    -H "Authorization: Bearer <YOUR_API_KEY>" \\
    -d '{
      "workflow_id": "${wfId}",
      "tables": {
${filteredTables.map((t, i) => {
  const tableName = t.name?.replace(/\.csv$/i, '') || `table${i+1}`
  return `        "${tableName}": "${buildTableCsvExample(t, 'curl')}"`
}).join(',\n')}
      }
    }'`
    : `curl -X POST "${baseUrl}/predict" \\
    -H "Content-Type: application/json" \\
    -H "Authorization: Bearer <YOUR_API_KEY>" \\
    -d '{
      "workflow_id": "${wfId}",
      "csv": "${csvExampleEscaped}"
    }'`

  // Init Python code
  const initPython = `import requests

HEADERS = {"Authorization": "Bearer <YOUR_API_KEY>"}

# Step 1: Initialize the model container
response = requests.post(
    "${baseUrl}/init",
    json={"workflow_id": "${wfId}"},
    headers=HEADERS,
    timeout=30
)
print("Init status:", response.json())`

  // Time-series Python example
  const predictPythonTS = isTimeSeries ? (isMultiTable
    ? `import requests
import pandas as pd

HEADERS = {"Authorization": "Bearer <YOUR_API_KEY>"}

# Time-series forecast (multi-table): send historical data + dimension tables
# Load your data files
tables = {}
${filteredTables.map((t, i) => {
  const tableName = t.name?.replace(/\.csv$/i, '') || `table${i+1}`
  return `df_${tableName} = pd.read_csv("${tableName}.csv")
tables["${tableName}"] = df_${tableName}.to_csv(index=False)`
}).join('\n')}

response = requests.post(
    "${baseUrl}/predict",
    json={
        "workflow_id": "${wfId}",
        "tables": tables
    },
    headers=HEADERS,
    timeout=60
)
# Returns forecast for prediction_length steps ahead with quantiles
print("Forecast:", response.json())`
    : `import requests
import pandas as pd

HEADERS = {"Authorization": "Bearer <YOUR_API_KEY>"}

# Time-series forecast: send historical data, get future predictions
# Key columns: ${tsSchema?.id_col} (series id), ${tsSchema?.timestamp_col} (datetime), ${tsSchema?.target_col} (target)
history_csv = """${tsCsvExamplePython}"""

response = requests.post(
    "${baseUrl}/predict",
    json={
        "workflow_id": "${wfId}",
        "csv": history_csv
    },
    headers=HEADERS,
    timeout=30
)
# Returns forecast for prediction_length steps ahead with quantiles
print("Forecast:", response.json())`) : null

  // Predict Python code - different for batch vs online AND single vs multi-table
  const predictPythonOnline = isMultiTable
    ? `import requests

HEADERS = {"Authorization": "Bearer <YOUR_API_KEY>"}

# Step 2: Make predictions (multi-table - online)
tables = {
${filteredTables.map((t, i) => {
  const tableName = t.name?.replace(/\.csv$/i, '') || `table${i+1}`
  return `    "${tableName}": "${buildTableCsvExample(t, 'python')}"`
}).join(',\n')}
}

response = requests.post(
    "${baseUrl}/predict",
    json={
        "workflow_id": "${wfId}",
        "tables": tables
    },
    headers=HEADERS,
    timeout=10
)
print("Prediction:", response.json())`
    : `import requests

HEADERS = {"Authorization": "Bearer <YOUR_API_KEY>"}

# Step 2: Make predictions (single row - online)
csv_data = """${csvHeader}
${csvDataRow}"""

response = requests.post(
    "${baseUrl}/predict",
    json={
        "workflow_id": "${wfId}",
        "csv": csv_data
    },
    headers=HEADERS,
    timeout=10
)
print("Prediction:", response.json())`

  const predictPythonBatch = isMultiTable
    ? `import requests
import pandas as pd

HEADERS = {"Authorization": "Bearer <YOUR_API_KEY>"}

# Step 2: Make predictions (multi-table batch)
# Load your data files
tables = {}
${filteredTables.map((t, i) => {
  const tableName = t.name?.replace(/\.csv$/i, '') || `table${i+1}`
  return `df_${tableName} = pd.read_csv("${tableName}.csv")
tables["${tableName}"] = df_${tableName}.to_csv(index=False)`
}).join('\n')}

response = requests.post(
    "${baseUrl}/predict",
    json={
        "workflow_id": "${wfId}",
        "tables": tables
    },
    headers=HEADERS,
    timeout=60
)
print("Predictions:", response.json())`
    : `import requests
import pandas as pd

HEADERS = {"Authorization": "Bearer <YOUR_API_KEY>"}

# Step 2: Make predictions (batch from DataFrame)
df = pd.read_csv("your_data.csv")

# Convert DataFrame to CSV string
csv_data = df.to_csv(index=False)

response = requests.post(
    "${baseUrl}/predict",
    json={
        "workflow_id": "${wfId}",
        "csv": csv_data
    },
    headers=HEADERS,
    timeout=60
)
print("Predictions:", response.json())`

  const predictPython = isTimeSeries
    ? predictPythonTS
    : (isBatch ? predictPythonBatch : predictPythonOnline)

  // Full Python example combining init + predict
  const fullPythonExample = isMultiTable
    ? `import requests
import time

WORKFLOW_ID = "${wfId}"
BASE_URL = "${baseUrl}"
HEADERS = {"Authorization": "Bearer <YOUR_API_KEY>"}

# Step 1: Initialize the model
print("Initializing model...")
init_response = requests.post(
    f"{BASE_URL}/init",
    json={"workflow_id": WORKFLOW_ID},
    headers=HEADERS,
    timeout=30
)
print("Init response:", init_response.json())

# Wait for container to be ready (optional polling)
for _ in range(30):
    status = requests.get(
        f"{BASE_URL}/status",
        params={"workflow_id": WORKFLOW_ID},
        headers=HEADERS
    ).json()
    if status.get("status") == "READY":
        print("Model is ready!")
        break
    time.sleep(2)

# Step 2: Make prediction (multi-table)
tables = {
${filteredTables.map((t, i) => {
  const tableName = t.name?.replace(/\.csv$/i, '') || `table${i+1}`
  return `    "${tableName}": "${buildTableCsvExample(t, 'python')}"`
}).join(',\n')}
}

predict_response = requests.post(
    f"{BASE_URL}/predict",
    json={
        "workflow_id": WORKFLOW_ID,
        "tables": tables
    },
    headers=HEADERS,
    timeout=${isBatch ? '60' : '10'}
)
print("Prediction result:", predict_response.json())`
    : `import requests
import time

WORKFLOW_ID = "${wfId}"
BASE_URL = "${baseUrl}"
HEADERS = {"Authorization": "Bearer <YOUR_API_KEY>"}

# Step 1: Initialize the model
print("Initializing model...")
init_response = requests.post(
    f"{BASE_URL}/init",
    json={"workflow_id": WORKFLOW_ID},
    headers=HEADERS,
    timeout=30
)
print("Init response:", init_response.json())

# Wait for container to be ready (optional polling)
for _ in range(30):
    status = requests.get(
        f"{BASE_URL}/status",
        params={"workflow_id": WORKFLOW_ID},
        headers=HEADERS
    ).json()
    if status.get("status") == "READY":
        print("Model is ready!")
        break
    time.sleep(2)

# Step 2: Make prediction
csv_data = """${csvHeader}
${csvDataRow}"""

predict_response = requests.post(
    f"{BASE_URL}/predict",
    json={
        "workflow_id": WORKFLOW_ID,
        "csv": csv_data
    },
    headers=HEADERS,
    timeout=${isBatch ? '60' : '10'}
)
print("Prediction result:", predict_response.json())`

  // Safe columns for display
  const safeColumns = Array.isArray(columns) ? columns : []

  return (
    <div className='flex h-full min-w-0 flex-col'>
      {/* Header */}
      <div className='flex items-center justify-between border-b border-ink/10 px-6 py-4'>
        <div>
          <div className='text-lg font-semibold text-ink'>{title}</div>
          {workflowId && (
            <div className='mt-1 text-xs text-ink'>
              {t('inferenceView.workflow')}: <span className='font-mono text-ink'>{workflowId}</span>
            </div>
          )}
        </div>
        <ModButton severity='secondary' onClick={onBack}>
          {t('inferenceView.back')}
        </ModButton>
      </div>

      {/* Content */}
      <div className='flex-1 space-y-6 overflow-auto p-6'>
        {/* Info banner */}
        <div className='rounded-lg border border-accent bg-accent/15 p-4'>
          <div className='flex items-start gap-3'>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='mt-0.5 flex-shrink-0 text-accent'>
              <circle cx='12' cy='12' r='10' />
              <line x1='12' y1='16' x2='12' y2='12' />
              <line x1='12' y1='8' x2='12.01' y2='8' />
            </svg>
            <div className='text-sm text-ink'>
              <strong className='text-ink'>{t('inferenceView.howItWorks')}:</strong> {t('inferenceView.howItWorksDesc')}
              {isBatch
                ? ` ${t('inferenceView.batchHint')}`
                : ` ${t('inferenceView.onlineHint')}`}
            </div>
          </div>
        </div>

        {/* Step 1: Init */}
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <span className='flex h-6 w-6 items-center justify-center rounded-full bg-accent/15 text-xs font-medium text-accent'>1</span>
            <span className='text-sm font-semibold text-ink'>{t('inferenceView.initModel')}</span>
          </div>
          <p className='text-sm text-ink'>
            {t('inferenceView.initDesc')}
          </p>
          <div className='grid gap-4 md:grid-cols-2'>
            <div>
              <div className='mb-2 text-xs font-medium text-ink'>cURL</div>
              <CodeSnippet language='bash' code={initCurl} />
            </div>
            <div>
              <div className='mb-2 text-xs font-medium text-ink'>Python</div>
              <CodeSnippet language='python' code={initPython} />
            </div>
          </div>
        </div>

        {/* Step 2: Predict */}
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <span className='flex h-6 w-6 items-center justify-center rounded-full bg-accent/15 text-xs font-medium text-accent'>2</span>
            <span className='text-sm font-semibold text-ink'>{t('inferenceView.makePredictions')}</span>
          </div>
          <p className='text-sm text-ink'>
            {t('inferenceView.predictDesc')}
            {isBatch
              ? ` ${t('inferenceView.batchOptimized')}`
              : ` ${t('inferenceView.onlineLowLatency')}`}
          </p>
          <div className='grid gap-4 md:grid-cols-2'>
            <div>
              <div className='mb-2 text-xs font-medium text-ink'>cURL</div>
              <CodeSnippet language='bash' code={predictCurl} />
            </div>
            <div>
              <div className='mb-2 text-xs font-medium text-ink'>Python</div>
              <CodeSnippet language='python' code={predictPython} />
            </div>
          </div>
        </div>

        {/* Full example */}
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
              <path d='M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z' />
              <polyline points='14,2 14,8 20,8' />
            </svg>
            <span className='text-sm font-semibold text-ink'>{t('inferenceView.fullExample')}</span>
          </div>
          <p className='text-sm text-ink'>
            {t('inferenceView.fullExampleDesc')}
          </p>
          <CodeSnippet language='python' code={fullPythonExample} />
        </div>

        {/* Schema info */}
        {filteredTables.length > 0 && (
          <div className='space-y-3'>
            <div className='flex items-center gap-2'>
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-amber-600'>
                <rect x='3' y='3' width='18' height='18' rx='2' ry='2' />
                <line x1='3' y1='9' x2='21' y2='9' />
                <line x1='9' y1='21' x2='9' y2='9' />
              </svg>
              <span className='text-sm font-semibold text-ink'>{t('inferenceView.expectedColumns')}</span>
              {targetCol && (
                <span className='ml-auto text-xs text-ink'>
                  {t('inferenceView.targetExcluded', { col: targetCol })}
                </span>
              )}
            </div>
            <div className='space-y-4'>
              {filteredTables.map((table, tableIdx) => (
                <div key={tableIdx} className='rounded-lg border border-ink/10 bg-ink/[0.03] p-4'>
                  {/* Table name (show for all tables, including single-table) */}
                  {table.name && (
                    <div className='mb-3 flex items-center gap-2 border-b border-ink/10 pb-2'>
                      <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                        <path d='M3 3h7v7H3V3zm11 0h7v7h-7V3zM3 14h7v7H3v-7zm11 0h7v7h-7v-7z' />
                      </svg>
                      <span className='text-sm font-medium text-ink'>
                        {t('inferenceView.tablePrefix')} {table.name.replace(/\.csv$/i, '')}
                      </span>
                    </div>
                  )}
                  {/* Columns */}
                  <div className='flex flex-wrap gap-2'>
                    {(table.columns || []).map((col, i) => {
                      const name = typeof col === 'string' ? col : (col?.name || 'column')
                      const type = typeof col === 'object' ? col?.type : null
                      return (
                        <span
                          key={i}
                          className='rounded-md bg-ink/[0.03] px-2 py-1 font-mono text-xs text-ink'
                        >
                          {name}
                          {type && <span className='ml-1 text-ink'>({type})</span>}
                        </span>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
